import "../../../css/CrawlSources.css";
import React, { useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Form, Input, InputNumber, Radio, TimePicker, Select, Button, message, Spin } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { getSource, createSource, updateSource } from "../../../services/polly/crawlSourceService";
import moment from "moment";

const { TextArea } = Input;
const { Option } = Select;

// --- Cron helpers ---

function cronToSchedule(cron) {
  if (!cron) return { type: "manual" };
  const parts = cron.split(/\s+/);
  if (parts.length < 6) return { type: "custom", customCron: cron };

  const [sec, min, hour, dayOfMonth, month, dayOfWeek] = parts;

  if (month === "*" && dayOfMonth === "*" && dayOfWeek === "*") {
    return { type: "daily", time: moment().hour(Number(hour)).minute(Number(min)) };
  }
  if (month === "*" && dayOfMonth === "*" && dayOfWeek !== "*") {
    return { type: "weekly", time: moment().hour(Number(hour)).minute(Number(min)), dayOfWeek };
  }
  if (month === "*" && dayOfMonth !== "*" && dayOfWeek === "?") {
    return { type: "monthly", time: moment().hour(Number(hour)).minute(Number(min)), dayOfMonth };
  }
  return { type: "custom", customCron: cron };
}

function scheduleToCron(schedule) {
  if (schedule.type === "manual") return null;
  if (schedule.type === "custom") return schedule.customCron || null;

  const h = schedule.time ? schedule.time.hour() : 0;
  const m = schedule.time ? schedule.time.minute() : 0;

  if (schedule.type === "daily") return `0 ${m} ${h} * * *`;
  if (schedule.type === "weekly") return `0 ${m} ${h} * * ${schedule.dayOfWeek || "MON"}`;
  if (schedule.type === "monthly") return `0 ${m} ${h} ${schedule.dayOfMonth || 1} * ?`;
  return null;
}

export default function CrawlSourceEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const history = useHistory();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleType, setScheduleType] = useState("manual");
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const source = await getSource(id);
        if (!mountedRef.current) return;
        const sched = cronToSchedule(source.scheduleCron);
        form.setFieldsValue({
          name: source.name,
          seedUrl: source.crawlSeedUrl,
          maxDepth: source.crawlMaxDepth ?? 3,
          requestsPerSecond: source.crawlRequestsPerSecond ?? 2,
          includePatterns: source.crawlIncludePathPatterns ? JSON.parse(source.crawlIncludePathPatterns) : [],
          excludePatterns: source.crawlExcludePathPatterns ? JSON.parse(source.crawlExcludePathPatterns) : [],
          userAgent: source.crawlUserAgent || "",
          customHeaders: source.crawlCustomHeaders || "",
          scheduleType: sched.type,
          scheduleTime: sched.time || null,
          dayOfWeek: sched.dayOfWeek || "MON",
          dayOfMonth: sched.dayOfMonth || "1",
          customCron: sched.customCron || "",
        });
        setScheduleType(sched.type);
      } catch (err) {
        if (!mountedRef.current) return;
        console.warn("Failed to load crawl source:", err);
        message.error("Failed to load crawl source");
      }
      if (mountedRef.current) setLoading(false);
    })();
  }, [id, isNew, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const schedule = {
        type: values.scheduleType,
        time: values.scheduleTime,
        dayOfWeek: values.dayOfWeek,
        dayOfMonth: values.dayOfMonth,
        customCron: values.customCron,
      };

      const payload = {
        name: values.name,
        crawlSeedUrl: values.seedUrl,
        crawlMaxDepth: values.maxDepth ?? 3,
        crawlRequestsPerSecond: values.requestsPerSecond ?? 2,
        crawlIncludePathPatterns: JSON.stringify((values.includePatterns || []).filter(Boolean)),
        crawlExcludePathPatterns: JSON.stringify((values.excludePatterns || []).filter(Boolean)),
        crawlUserAgent: values.userAgent || null,
        crawlCustomHeaders: values.customHeaders || null,
        scheduleCron: scheduleToCron(schedule),
      };

      if (isNew) {
        await createSource(payload);
        message.success("Crawl source created");
      } else {
        await updateSource(id, payload);
        message.success("Crawl source updated");
      }
      history.push("/polly/crawl-sources");
    } catch (err) {
      if (err.errorFields) return; // form validation error
      console.warn("Failed to save crawl source:", err);
      message.error("Failed to save crawl source");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="crawl-edit-container" style={{ textAlign: "center", paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="crawl-edit-container">
      <div className="crawl-edit-breadcrumb">
        <a onClick={() => history.push("/polly/crawl-sources")}>Crawl Sources</a> &rsaquo; <span>{isNew ? "New Source" : "Edit Source"}</span>
      </div>
      <div className="crawl-edit-body">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            maxDepth: 3,
            requestsPerSecond: 2,
            scheduleType: "manual",
            dayOfWeek: "MON",
            dayOfMonth: "1",
            includePatterns: [],
            excludePatterns: [],
          }}
        >
          {/* Identity */}
          <div className="crawl-edit-section">
            <h3>Identity</h3>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: "Name is required" }]}>
              <Input placeholder="e.g. Main website" />
            </Form.Item>
            <Form.Item name="seedUrl" label="Seed URL" rules={[{ required: true, message: "Seed URL is required" }]}>
              <Input placeholder="https://example.com" />
            </Form.Item>
          </div>

          {/* Crawl Behavior */}
          <div className="crawl-edit-section">
            <h3>Crawl Behavior</h3>
            <Form.Item name="maxDepth" label="Max Depth">
              <InputNumber min={1} max={20} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="requestsPerSecond" label="Requests / second">
              <InputNumber min={0.1} max={50} step={0.5} style={{ width: 120 }} />
            </Form.Item>

            <Form.Item label="Include Patterns">
              <Form.List name="includePatterns">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div key={field.key} className="crawl-edit-pattern-row">
                        <Form.Item {...field} noStyle>
                          <Input placeholder="/docs/**" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      </div>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small">
                      Add include pattern
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item label="Exclude Patterns">
              <Form.List name="excludePatterns">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div key={field.key} className="crawl-edit-pattern-row">
                        <Form.Item {...field} noStyle>
                          <Input placeholder="/admin/**" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      </div>
                    ))}
                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small">
                      Add exclude pattern
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>
          </div>

          {/* Schedule */}
          <div className="crawl-edit-section">
            <h3>Schedule</h3>
            <Form.Item name="scheduleType">
              <Radio.Group onChange={(e) => setScheduleType(e.target.value)}>
                <Radio value="manual">Manual</Radio>
                <Radio value="daily">Daily</Radio>
                <Radio value="weekly">Weekly</Radio>
                <Radio value="monthly">Monthly</Radio>
                <Radio value="custom">Custom cron</Radio>
              </Radio.Group>
            </Form.Item>

            {(scheduleType === "daily" || scheduleType === "weekly" || scheduleType === "monthly") && (
              <Form.Item name="scheduleTime" label="Time">
                <TimePicker format="HH:mm" />
              </Form.Item>
            )}

            {scheduleType === "weekly" && (
              <Form.Item name="dayOfWeek" label="Day of Week">
                <Select style={{ width: 160 }}>
                  <Option value="MON">Monday</Option>
                  <Option value="TUE">Tuesday</Option>
                  <Option value="WED">Wednesday</Option>
                  <Option value="THU">Thursday</Option>
                  <Option value="FRI">Friday</Option>
                  <Option value="SAT">Saturday</Option>
                  <Option value="SUN">Sunday</Option>
                </Select>
              </Form.Item>
            )}

            {scheduleType === "monthly" && (
              <Form.Item name="dayOfMonth" label="Day of Month">
                <Select style={{ width: 120 }}>
                  {Array.from({ length: 28 }, (_, i) => (
                    <Option key={i + 1} value={String(i + 1)}>{i + 1}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {scheduleType === "custom" && (
              <Form.Item name="customCron" label="Cron Expression (6-field)" rules={[{ required: true, message: "Cron expression is required" }]}>
                <Input placeholder="0 0 2 * * *" />
              </Form.Item>
            )}
          </div>

          {/* Request Identity */}
          <div className="crawl-edit-section">
            <h3>Request Identity</h3>
            <Form.Item name="userAgent" label="User Agent">
              <Input placeholder="Leave blank for default" />
            </Form.Item>
            <Form.Item name="customHeaders" label="Custom Headers (one per line, Header: Value)">
              <TextArea rows={3} placeholder="X-Custom-Header: value" />
            </Form.Item>
          </div>

          <div className="crawl-edit-actions">
            <Button type="primary" loading={submitting} onClick={handleSave}>
              {isNew ? "Create Source" : "Save Changes"}
            </Button>
            <Button onClick={() => history.push("/polly/crawl-sources")}>Cancel</Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
